import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { inpatientAPI, syncVisitServicesToVaccinations, clinicAppointmentsAPI } from '../../services/api';
import { showErrorToast, showSuccessToast } from '../../utils/notificationUtils';
import DashboardTab from './InpatientDetails/sections/DashboardTab';
import ServicesTab from './InpatientDetails/sections/ServicesTab';
import SOAPDetailsContent from '../reception/SOAPDetailsContent';
import MedicalHistoryTab from './InpatientDetails/sections/MedicalHistoryTab';
import DailyChecklistTab from './InpatientDetails/sections/DailyChecklistTab';
import ChiefComplaintTab from './InpatientDetails/sections/ChiefComplaintTab';
import DiagnosisTab from './InpatientDetails/sections/DiagnosisTab';
import VetOrdersTab from './InpatientDetails/sections/VetOrdersTab';
import AccommodationTab from './InpatientDetails/sections/AccommodationTab';
import DailyCareTab from './InpatientDetails/sections/DailyCareTab';
import AttachmentsTab from './InpatientDetails/sections/AttachmentsTab';
import SpecialRequestsTab from './InpatientDetails/sections/SpecialRequestsTab';
import InpatientCheckoutModal from './InpatientCheckoutModal';
import './InpatientDetailsModal.css';

const InpatientDetailsModal = ({
    isOpen,
    inpatientId,
    onClose,
    workingHours = [],
    fetchAllTabData
}) => {
    const { t } = useTranslation();
    const [activeTab, setActiveTab] = useState('summary');
    const [loading, setLoading] = useState(false);
    const [overlayData, setOverlayData] = useState(null);
    const [error, setError] = useState(null);
    const [selectedServices, setSelectedServices] = useState([]);
    const selectedServicesRef = useRef([]);
    const [savingAll, setSavingAll] = useState(false);
    const tabRef = useRef(null);

    // Modal states
    const [showCheckout, setShowCheckout] = useState(false);

    const isCheckoutStatus = useMemo(() => {
        const status = overlayData?.status?.toLowerCase();
        return status === 'checkout' || status === 'closed' || status === 'completed';
    }, [overlayData]);

    const normalizeVisitDetailsToServices = useCallback((visitDetails = []) => {
        if (!Array.isArray(visitDetails)) return [];
        return visitDetails.map(vd => {
            const clinicService = vd?.clinic_service || vd?.clinicService || null;
            const clinicServiceId = clinicService?.id ?? vd?.clinic_service_id ?? vd?.clinicServiceId ?? vd?.clinic_service?.id;
            return {
                id: clinicServiceId,
                inpatient_visit_detail_id: vd?.id,
                uniqueKey: `vd-${vd?.id}`,
                service_name_en: clinicService?.service_name_en ?? clinicService?.serviceNameEn ?? vd?.service_name_en,
                service_name_ar: clinicService?.service_name_ar ?? clinicService?.serviceNameAr ?? vd?.service_name_ar,
                service_code: clinicService?.service_code ?? clinicService?.serviceCode ?? vd?.service_code,
                service_type: clinicService?.service_type ?? clinicService?.serviceType ?? vd?.service_type,
                service_list_id: clinicService?.service_list_id ?? vd?.service_list_id ?? null,
                medication_category: clinicService?.medication_category ?? vd?.medication_category ?? null,
                price: parseFloat(vd?.price ?? clinicService?.price ?? 0),
                quantity: parseInt(vd?.qty ?? vd?.quantity ?? 1),
                discount: parseFloat(vd?.discount ?? 0),
                notes: vd?.notes || '',
                due_date: vd?.due_date ?? null,
                is_fulfilled: !!(vd?.is_fulfilled ?? vd?.isFulfilled),
                has_vat: !!(clinicService?.has_vat ?? clinicService?.hasVat),
                vat_percentage: clinicService?.vat_percentage ?? clinicService?.vatPercentage ?? 0,
                fulfilled_at: vd?.fulfilled_at,
                fulfilled_by_user_id: vd?.fulfilled_by_user_id,
            };
        }).filter(s => !!s.id);
    }, []);

    const fetchDetails = useCallback(async () => {
        if (!inpatientId || !isOpen) return;

        setLoading(true);
        try {
            // Fetch both the rich details endpoint and the main visit record.
            // The main visit record carries accommodation/stay fields (length_of_stay_days, billable_stay_days, etc.)
            // that the details endpoint may omit.
            const [response, visitRecordResp] = await Promise.all([
                fetch(`${import.meta.env.VITE_API_URL}/api/v1/inpatient-visit-details/${inpatientId}?t=${new Date().getTime()}`, {
                    headers: {
                        'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
                        'Accept': 'application/json'
                    }
                }),
                inpatientAPI.getInpatientVisit(inpatientId).catch(() => null)
            ]);

            const visitData = visitRecordResp?.data?.status === 'success' ? visitRecordResp.data.data : null;

            if (response.ok) {
                const result = await response.json();
                if (result.status === 'success' && result.data) {
                    // Merge stay/accommodation fields from the main visit record so day tabs stay in sync.
                    const mergedData = {
                        ...result.data,
                        ...(visitData ? {
                            length_of_stay_days: visitData.length_of_stay_days ?? result.data.length_of_stay_days,
                            billable_stay_days: visitData.billable_stay_days ?? result.data.billable_stay_days,
                            cage_no: visitData.cage_no ?? result.data.cage_no,
                            is_isolated: visitData.is_isolated ?? result.data.is_isolated,
                            accommodation_details: visitData.accommodation_details ?? result.data.accommodation_details,
                            checked_out_at: visitData.checked_out_at ?? result.data.checked_out_at,
                            checkout_date: visitData.checkout_date ?? result.data.checkout_date,
                        } : {})
                    };
                    setOverlayData(mergedData);
                    setLiveStayDays(null); // fresh server data is the source again

                    if (result.data.visit_details) {
                        const rawVisitDetails = result.data.visit_details;
                        const missingFulfillmentField = Array.isArray(rawVisitDetails)
                            ? rawVisitDetails.some(vd => vd?.is_fulfilled === undefined && vd?.isFulfilled === undefined)
                            : false;

                        // Fallback: some backends omit is_fulfilled in this endpoint's visit_details.
                        // If so, fetch the dedicated details endpoint that includes fulfillment.
                        if (missingFulfillmentField) {
                            try {
                                const detailsResp = await inpatientAPI.getInpatientVisitDetails(inpatientId);
                                const detailsPayload = detailsResp?.data;
                                const detailsArray =
                                    detailsPayload?.data ??
                                    detailsPayload ??
                                    [];

                                if (Array.isArray(detailsArray)) {
                                    const normalizedServices = normalizeVisitDetailsToServices(detailsArray);
                                    setSelectedServices(normalizedServices);
                                    selectedServicesRef.current = normalizedServices;
                                } else {
                                    const normalizedServices = normalizeVisitDetailsToServices(rawVisitDetails);
                                    setSelectedServices(normalizedServices);
                                    selectedServicesRef.current = normalizedServices;
                                }
                            } catch (e) {
                                const normalizedServices = normalizeVisitDetailsToServices(rawVisitDetails);
                                setSelectedServices(normalizedServices);
                                selectedServicesRef.current = normalizedServices;
                            }
                        } else {
                            const normalizedServices = normalizeVisitDetailsToServices(rawVisitDetails);
                            setSelectedServices(normalizedServices);
                            selectedServicesRef.current = normalizedServices;
                        }
                    }
                }
            } else {
                // If this endpoint fails (e.g. backend selecting columns that aren't migrated yet),
                // fall back to the more stable inpatient visit show endpoint so the UI can still load.
                try {
                    const fallbackResp = await inpatientAPI.getInpatientVisit(inpatientId);
                    const fallbackPayload = fallbackResp?.data;
                    if (fallbackPayload?.status === 'success' && fallbackPayload?.data) {
                        setOverlayData(fallbackPayload.data);

                        if (fallbackPayload.data.visit_details) {
                            const normalized = normalizeVisitDetailsToServices(fallbackPayload.data.visit_details);
                            setSelectedServices(normalized);
                            selectedServicesRef.current = normalized;
                        }
                        setError(null);
                    } else {
                        const errJson = await response.json().catch(() => ({}));
                        setError(errJson?.message || t('receptionInpatients.details.toasts.loadFailed'));
                    }
                } catch (e) {
                    const errJson = await response.json().catch(() => ({}));
                    setError(errJson?.message || t('receptionInpatients.details.toasts.loadFailed'));
                }
            }
        } catch (err) {
            console.error(err);
            setError(t('receptionInpatients.details.toasts.loadFailed'));
        } finally {
            setLoading(false);
        }
    }, [inpatientId, isOpen, t, normalizeVisitDetailsToServices]);

    useEffect(() => {
        if (isOpen) {
            fetchDetails();
        } else {
            setOverlayData(null);
            setSelectedServices([]);
            selectedServicesRef.current = [];
            setActiveTab('summary');
        }
    }, [isOpen, fetchDetails]);

    const handleAddService = (service, overrides = {}) => {
        const current = selectedServicesRef.current || [];
        // Check for duplicates considering due_date
        const isDuplicate = current.some(s =>
            s.id === service.id &&
            s.due_date === (overrides.due_date || null)
        );

        if (isDuplicate) {
            showErrorToast(t('receptionInpatients.details.toasts.serviceAlreadyAdded'));
            return;
        }
        const newService = {
            ...service,
            service_list_id: service.service_list_id ?? null,
            medication_category: service.medication_category ?? null,
            quantity: 1,
            discount: 0,
            notes: '',
            is_fulfilled: false,
            price: parseFloat(service.price || 0),
            ...overrides,
            // Ensure unique key for React map
            uniqueKey: overrides.uniqueKey || `${service.id}-${overrides.due_date || 'nodate'}-${new Date().getTime()}`
        };
        const next = [...current, newService];
        selectedServicesRef.current = next;
        setSelectedServices(next);
    };

    const handleUpdateService = (identifier, updates) => {
        const current = selectedServicesRef.current || [];
        const next = current.map(s => {
            // Match by uniqueKey if available, otherwise fallback to id
            const match = s.uniqueKey ? s.uniqueKey === identifier : s.id === identifier;
            return match ? { ...s, ...updates } : s;
        });
        selectedServicesRef.current = next;
        setSelectedServices(next);
    };

    const handleRemoveService = (identifier) => {
        const current = selectedServicesRef.current || [];
        const next = current.filter(s => {
            const match = s.uniqueKey ? s.uniqueKey === identifier : s.id === identifier;
            return !match;
        });
        selectedServicesRef.current = next;
        setSelectedServices(next);
    };

    const handleSaveAll = async (silent = false) => {
        if (!inpatientId) return;
        setSavingAll(true);
        try {
            const currentServices = selectedServicesRef.current || [];
            const payload = {
                initial_diag: overlayData?.initial_diag || '',
                new_services: currentServices.map(s => ({
                    inpatient_visit_id: parseInt(inpatientId),
                    clinic_service_id: s.id,
                    qty: s.quantity,
                    price: s.price,
                    discount: s.discount,
                    notes: s.notes,
                    is_fulfilled: s.is_fulfilled ? 1 : 0,
                    due_date: s.due_date || null
                }))
            };

            // 1. Trigger active tab save if supported
            if (tabRef.current && typeof tabRef.current.save === 'function') {
                await tabRef.current.save();
            } else {
                console.log('[InpatientDetailsModal] No tab save method available', {
                    hasRef: !!tabRef.current,
                    hasSave: tabRef.current && typeof tabRef.current.save === 'function',
                    activeTab
                });
            }

            // 2. Save services
            const resp = await inpatientAPI.addServiceToInpatientVisit(payload);

            // If backend returns updated rows, sync local state without requiring a full refetch.
            // Supported shapes:
            // - { status: 'success', data: { visit_details: [...] } }
            // - { status: 'success', data: [...] }  (array of visit detail rows)
            const result = resp?.data;
            const maybeVisitDetails =
                result?.data?.visit_details ??
                (Array.isArray(result?.data) ? result.data : null);
            if (maybeVisitDetails) {
                const normalized = normalizeVisitDetailsToServices(maybeVisitDetails);
                setSelectedServices(normalized);
                selectedServicesRef.current = normalized;
            }

            if (!silent) showSuccessToast(t('receptionInpatients.details.toasts.saveSuccess'));
            if (fetchAllTabData) fetchAllTabData(true);
            // For silent autosaves (Daily Checklist), avoid refetching details immediately.
            // If the details endpoint lags behind the upsert/update response, refetching can incorrectly revert UI state.
            if (!silent) {
                await fetchDetails(); // Ensure overlayData + any server-side computed fields stay in sync
            }
            return true;
        } catch (err) {
            console.error(err);
            if (!silent) showErrorToast(t('receptionInpatients.details.toasts.saveFailed'));
            return false;
        } finally {
            setSavingAll(false);
        }
    };

    const grandTotal = useMemo(() => {
        return selectedServices.reduce((sum, s) => {
            if (!s.is_fulfilled) return sum;
            const subtotal = s.quantity * s.price - (s.discount || 0);
            const vat = s.has_vat ? (subtotal * parseFloat(s.vat_percentage || 0)) / 100 : 0;
            return sum + subtotal + vat;
        }, 0);
    }, [selectedServices]);

    // Calculate accommodation fees (rate_per_day × stay days). While the
    // accommodation tab is being edited, the in-progress days value previews
    // live so the footer total reacts immediately.
    const [liveStayDays, setLiveStayDays] = useState(null);
    const accommodationFees = useMemo(() => {
        const ratePerDay = parseFloat(overlayData?.rate_per_day || 0);
        const billableDays = liveStayDays !== null
            ? liveStayDays
            // Manual length_of_stay_days takes precedence (same rule as the
            // backend's effective stay days); date-derived is the fallback.
            : parseInt(overlayData?.length_of_stay_days || overlayData?.billable_stay_days || 0);
        if (ratePerDay > 0 && billableDays > 0) {
            return ratePerDay * billableDays;
        }
        return 0;
    }, [overlayData?.rate_per_day, overlayData?.billable_stay_days, overlayData?.length_of_stay_days, liveStayDays]);

    const displayedTotal = useMemo(() => {
        // At checkout, backend is the source of truth (services + stay charges).
        if (isCheckoutStatus) {
            const v = overlayData?.final_total_amount ?? overlayData?.total_amount;
            const n = parseFloat(v);
            if (!Number.isNaN(n)) return n;
        }
        // Before checkout: services total + accommodation fees
        return grandTotal + accommodationFees;
    }, [grandTotal, accommodationFees, isCheckoutStatus, overlayData?.final_total_amount, overlayData?.total_amount]);

    const stayTotal = useMemo(() => {
        const n = parseFloat(overlayData?.stay_total);
        return Number.isNaN(n) ? null : n;
    }, [overlayData?.stay_total]);

    // Helper function to resolve pet profile image URL
    const getPetImageUrl = useCallback((pet) => {
        if (!pet) return null;

        // Handle profile_image_path which might be a string or an object
        const profilePath = typeof pet.profile_image_path === "string"
            ? pet.profile_image_path
            : pet.profile_image_path?.url || pet.profile_image_path?.path;

        // Try multiple possible image fields
        const imageUrl =
            pet.primaryImageUrl ||
            pet.primary_image_url ||
            profilePath ||
            pet.profile_image_url ||
            pet.profileImageUrl ||
            pet.image ||
            pet.avatar;

        // If we have a URL, check if it needs the API base URL
        if (imageUrl && typeof imageUrl === 'string' && imageUrl.trim()) {
            const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000';
            // Check if it's already a full URL
            if (imageUrl.startsWith('http://') || imageUrl.startsWith('https://')) {
                return imageUrl;
            }
            // If it's a relative path, prepend API URL
            const cleanPath = imageUrl.startsWith('/') ? imageUrl : `/${imageUrl}`;
            return `${API_BASE_URL}${cleanPath}`;
        }

        return null;
    }, []);

    const [imageError, setImageError] = useState(false);
    const petImageUrl = useMemo(() => getPetImageUrl(overlayData?.pet), [overlayData?.pet, getPetImageUrl]);
    const petInitial = overlayData?.pet?.name?.charAt(0) || 'P';

    // Header attribute line — same formatting as the Summary tab's Pet card
    const petAgeDisplay = useMemo(() => {
        const raw = overlayData?.pet?.age;
        if (raw === null || raw === undefined || raw === '') return '---';
        const toYears = (n) => t('common.ageYears', { count: n, defaultValue: `${n} years` });
        const toMonths = (n) => t('common.ageMonths', { count: n, defaultValue: `${n} months` });
        if (typeof raw === 'number' && Number.isFinite(raw)) return toYears(Math.max(0, Math.round(raw)));
        const s = String(raw).trim();
        const n = parseFloat(s.replace(/[^\d.]/g, ''));
        if (Number.isNaN(n)) return s || '---';
        const lower = s.toLowerCase();
        const hasYear = lower.includes('year') || lower.includes('سنة') || lower.includes('سنوات');
        const hasMonth = lower.includes('month') || lower.includes('شهر') || lower.includes('أشهر');
        if (hasYear) return toYears(Math.max(0, Math.round(n)));
        if (hasMonth) return toMonths(Math.max(0, Math.round(n)));
        return toYears(Math.max(0, Math.round(n)));
    }, [overlayData?.pet?.age, t]);

    const petGenderDisplay = useMemo(() => {
        const raw = overlayData?.pet?.gender;
        if (raw === null || raw === undefined || raw === '') return '---';
        const s = String(raw).trim();
        const k = s.toLowerCase();
        const normalized = (k === 'm' || k === 'male') ? 'male' : (k === 'f' || k === 'female') ? 'female' : null;
        if (!normalized) return s;
        return t(`completeProfile.gender.options.${normalized}`, { defaultValue: s });
    }, [overlayData?.pet?.gender, t]);

    const petAttributesLine = useMemo(() => {
        const pet = overlayData?.pet;
        if (!pet) return '';
        return [
            pet.species?.name || '---',
            pet.breed?.name || '---',
            petGenderDisplay,
            petAgeDisplay,
            pet.neutered ? t('common.yes', { defaultValue: 'Yes' }) : t('common.no', { defaultValue: 'No' }),
        ].join(' · ');
    }, [overlayData?.pet, petGenderDisplay, petAgeDisplay, t]);

    // Reset image error when pet changes
    useEffect(() => {
        setImageError(false);
    }, [overlayData?.pet?.id, petImageUrl]);

    if (!isOpen) return null;

    return (
        <div className={`inpatient-modal-overlay ${isOpen ? 'active' : ''}`} onClick={onClose}>
            <div className="inpatient-modal-container" onClick={e => e.stopPropagation()}>
                {/* Header */}
                <div className="inpatient-modal-header">
                    <div className="header-info">
                        <div className="pet-avatar-mini">
                            {petImageUrl && !imageError ? (
                                <img
                                    src={petImageUrl}
                                    alt={overlayData?.pet?.name || 'Pet'}
                                    style={{
                                        width: '100%',
                                        height: '100%',
                                        objectFit: 'cover',
                                        borderRadius: '12px'
                                    }}
                                    onError={() => {
                                        setImageError(true);
                                    }}
                                />
                            ) : (
                                <div style={{
                                    display: 'flex',
                                    width: '100%',
                                    height: '100%',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                }}>
                                    {petInitial}
                                </div>
                            )}
                        </div>
                        <div className="header-text">
                            <h2>{overlayData?.pet?.name || t('common.loading')}</h2>
                            {petAttributesLine && (
                                <p className="inpatient-header-sub" title={petAttributesLine}>{petAttributesLine}</p>
                            )}
                        </div>
                    </div>
                    <div className="header-actions">
                        <button className="close-modal-btn" onClick={onClose}>
                            <i className="fas fa-times"></i>
                        </button>
                    </div>
                </div>

                {/* Content Area */}
                <div className="inpatient-modal-body">
                    <div className="modal-sidebar">
                        <nav>
                            <button
                                className={`nav-item ${activeTab === 'summary' ? 'active' : ''}`}
                                onClick={() => setActiveTab('summary')}
                            >
                                <i className="fas fa-th-large"></i>
                                <span>{t('receptionInpatients.details.tabs.summary')}</span>
                            </button>
                            <button
                                className={`nav-item ${activeTab === 'soap' ? 'active' : ''}`}
                                onClick={() => setActiveTab('soap')}
                            >
                                <i className="fas fa-notes-medical"></i>
                                <span>{t('receptionInpatients.details.tabs.soap')}</span>
                            </button>
                            <button
                                className={`nav-item ${activeTab === 'emr' ? 'active' : ''}`}
                                onClick={() => setActiveTab('emr')}
                            >
                                <i className="fas fa-file-medical"></i>
                                <span>{t('receptionInpatients.details.tabs.emr')}</span>
                            </button>

                            <div className="nav-divider"></div>

                            {/* Editable tabs remain visible after checkout/close for view-only access */}
                            <button
                                className={`nav-item ${activeTab === 'chief_complaint' ? 'active' : ''}`}
                                onClick={() => setActiveTab('chief_complaint')}
                            >
                                <i className="fas fa-comment-medical"></i>
                                <span>{t('receptionInpatients.details.tabs.chiefComplaint', { defaultValue: 'Chief Complaint' })}</span>
                            </button>
                            <button
                                className={`nav-item ${activeTab === 'daily_care' ? 'active' : ''}`}
                                onClick={() => setActiveTab('daily_care')}
                            >
                                <i className="fas fa-clipboard-check"></i>
                                <span>{t('receptionInpatients.details.tabs.dailyCare', { defaultValue: 'Daily Care' })}</span>
                            </button>
                            <button
                                className={`nav-item ${activeTab === 'diagnosis' ? 'active' : ''}`}
                                onClick={() => setActiveTab('diagnosis')}
                            >
                                <i className="fas fa-stethoscope"></i>
                                <span>{t('receptionInpatients.details.tabs.diagnosis', { defaultValue: 'Diagnosis' })}</span>
                            </button>
                            <button
                                className={`nav-item ${activeTab === 'accommodation' ? 'active' : ''}`}
                                onClick={() => setActiveTab('accommodation')}
                            >
                                <i className="fas fa-bed"></i>
                                <span>{t('receptionInpatients.details.tabs.accommodation', { defaultValue: 'Accommodation' })}</span>
                            </button>
                            <button
                                className={`nav-item ${activeTab === 'vet_orders' ? 'active' : ''}`}
                                onClick={() => setActiveTab('vet_orders')}
                            >
                                <i className="fas fa-user-md"></i>
                                <span>{t('receptionInpatients.details.tabs.vetOrders', { defaultValue: 'Vet Orders' })}</span>
                            </button>
                            <button
                                className={`nav-item ${activeTab === 'treatment_plan' ? 'active' : ''}`}
                                onClick={() => setActiveTab('treatment_plan')}
                            >
                                <i className="fas fa-pills"></i>
                                <span>{t('receptionInpatients.details.tabs.treatmentPlan', { defaultValue: 'Treatment Plan' })}</span>
                            </button>
                            <button
                                className={`nav-item ${activeTab === 'services' ? 'active' : ''}`}
                                onClick={() => setActiveTab('services')}
                            >
                                <i className="fas fa-concierge-bell"></i>
                                <span>{t('receptionInpatients.details.tabs.dailyServices', { defaultValue: 'Daily Services' })}</span>
                            </button>
                            <button
                                className={`nav-item ${activeTab === 'attachments' ? 'active' : ''}`}
                                onClick={() => setActiveTab('attachments')}
                            >
                                <i className="fas fa-paperclip"></i>
                                <span>{t('receptionInpatients.details.tabs.attachments', { defaultValue: 'Attachments' })}</span>
                            </button>
                            {!isCheckoutStatus && (
                                <button
                                    className="nav-item nav-item-checkout"
                                    onClick={() => setShowCheckout(true)}
                                    style={{ marginTop: 'auto', borderTop: '1px solid #E5E7EB', color: '#059669' }}
                                >
                                    <i className="fas fa-check-double"></i>
                                    <span>{t('receptionInpatients.details.buttons.checkout', { defaultValue: 'Check Out' })}</span>
                                </button>
                            )}
                        </nav>
                    </div>

                    <div className="modal-main-content">
                        {loading ? (
                            <div className="skel-content" aria-busy="true">
                                <div className="visit-skel skel-block h20 w30" />
                                <div className="visit-skel skel-block card" />
                                <div className="visit-skel skel-block w90" />
                                <div className="visit-skel skel-block w70" />
                                <div className="visit-skel skel-block w50" />
                                <div className="visit-skel skel-block card" />
                            </div>
                        ) : error ? (
                            <div className="error-state">
                                <p>{error}</p>
                                <button onClick={fetchDetails}>{t('receptionInpatients.details.retry')}</button>
                            </div>
                        ) : (
                            <div className="tab-pane-content">
                                {activeTab === 'summary' && <DashboardTab data={overlayData} t={t} />}
                                {activeTab === 'soap' && (
                                    <div className="soap-tab-integrated">
                                        <SOAPDetailsContent
                                            isVisible={true}
                                            inpatientId={inpatientId}
                                            readOnly={true}
                                            // Only include fulfilled services in Plan, and only at checkout/close.
                                            // Before checkout, keep the Plan section unchanged (no services checklist).
                                            showPlanServices={isCheckoutStatus}
                                            planServicesMode="fulfilledOnly"
                                            allowAutoLoadServices={false}
                                            services={isCheckoutStatus ? selectedServices : []}
                                            onSave={() => {
                                                fetchDetails();
                                                showSuccessToast(t('receptionInpatients.details.toasts.soapSaved', { defaultValue: 'SOAP notes saved successfully' }));
                                            }}
                                            hideHeader={true}
                                            visitInfo={{
                                                serial: overlayData?.in_serial_num || '',
                                                petName: overlayData?.pet?.name || '',
                                                ownerName: overlayData?.pet_owner?.full_name || '',
                                                visitDate: overlayData?.visit_date || ''
                                            }}
                                            petId={overlayData?.pet?.id}
                                        />
                                    </div>
                                )}
                                {activeTab === 'emr' && <MedicalHistoryTab petId={overlayData?.pet?.id} />}

                                {/* Editable Tabs */}
                                {activeTab === 'chief_complaint' && <ChiefComplaintTab ref={tabRef} inpatientId={inpatientId} readOnly={isCheckoutStatus} />}
                                {activeTab === 'daily_care' && (
                                    <DailyCareTab
                                        ref={tabRef}
                                        inpatientId={inpatientId}
                                        visitDate={overlayData?.visit_date}
                                        checkoutDate={overlayData?.checked_out_at || overlayData?.checkout_date}
                                        visitStatus={overlayData?.status}
                                        readOnly={isCheckoutStatus}
                                    />
                                )}
                                {activeTab === 'diagnosis' && <DiagnosisTab ref={tabRef} inpatientId={inpatientId} readOnly={isCheckoutStatus} />}
                                {activeTab === 'accommodation' && <AccommodationTab ref={tabRef} inpatientId={inpatientId} onSave={fetchDetails} onDurationChange={setLiveStayDays} readOnly={isCheckoutStatus} />}
                                {activeTab === 'vet_orders' && (
                                    <VetOrdersTab
                                        ref={tabRef}
                                        inpatientId={inpatientId}
                                        readOnly={isCheckoutStatus}
                                    />
                                )}
                                {activeTab === 'treatment_plan' && (
                                    <ServicesTab
                                        services={selectedServices}
                                        onAddService={handleAddService}
                                        onUpdateService={handleUpdateService}
                                        onRemoveService={handleRemoveService}
                                        clinicServices={overlayData?.clinic?.services}
                                        readOnly={isCheckoutStatus}
                                        visitDate={overlayData?.visit_date}
                                        lengthOfStay={parseInt(overlayData?.length_of_stay_days || overlayData?.billable_stay_days || 0)}
                                    />
                                )}
                                {activeTab === 'services' && (
                                    <DailyChecklistTab
                                        inpatientId={inpatientId}
                                        visitDate={overlayData?.visit_date}
                                        readOnly={isCheckoutStatus}
                                        lengthOfStay={parseInt(overlayData?.length_of_stay_days || overlayData?.billable_stay_days || 0)}
                                        services={selectedServices}
                                        clinicServices={overlayData?.clinic?.services}
                                        specialRequestsNote={overlayData?.special_requests_note || ''}
                                        onAddService={handleAddService}
                                        onUpdateService={handleUpdateService}
                                        onRemoveService={handleRemoveService}
                                        onAutoSave={handleSaveAll}
                                    />
                                )}
                                {activeTab === 'attachments' && (
                                    <AttachmentsTab
                                        inpatientId={inpatientId}
                                        readOnly={isCheckoutStatus}
                                    />
                                )}
                            </div>
                        )}
                    </div>
                </div>

                {/* Footer Actions */}
                <div className="inpatient-modal-footer">
                    <div className="total-summary">
                        <span className="total-label">{t('receptionInpatients.details.summary.grandTotal')}</span>
                        <span className="total-value">{displayedTotal.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                        {isCheckoutStatus && stayTotal !== null && (
                            <div className="total-subline" style={{ fontSize: '0.8rem', color: '#64748b', marginTop: 2 }}>
                                {t('receptionInpatients.details.summary.stayCharges', { defaultValue: 'Stay charges' })}: {stayTotal.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                            </div>
                        )}
                    </div>
                    <div className="footer-btns">
                        {/* Cancel removed — the header × closes the modal.
                            Save hides entirely on completed / checkout stays. */}
                        {!isCheckoutStatus && (
                            <button
                                className="btn-primary"
                                onClick={handleSaveAll}
                                disabled={savingAll}
                            >
                                <i className={savingAll ? "fas fa-spinner fa-spin" : "fas fa-save"}></i>
                                {savingAll ? t('common.saving') : t('common.save')}
                            </button>
                        )}
                    </div>
                </div>
            </div>



            <InpatientCheckoutModal
                isOpen={showCheckout}
                onClose={() => setShowCheckout(false)}
                inpatientId={inpatientId}
                onSave={async (checkoutData) => {
                    await handleSaveAll(true);

                    // Create follow-up appointment if required
                    if (checkoutData?.follow_up_required && checkoutData?.follow_up_date && checkoutData?.follow_up_time) {
                        try {
                            const appointmentPayload = {
                                pet_id: overlayData?.pet?.id,
                                clinic_id: overlayData?.clinic_id || overlayData?.clinic?.id,
                                vet_id: overlayData?.vet_id || overlayData?.vet?.id || null,
                                appointment_at: `${checkoutData.follow_up_date} ${checkoutData.follow_up_time}:00`,
                                pet_owner_id: overlayData?.pet_owner_id || overlayData?.pet_owner?.id,
                                reason: `Follow-up after inpatient visit`,
                                appointment_type_id: checkoutData.follow_up_type || '3',
                            };
                            await clinicAppointmentsAPI.createAppointment(appointmentPayload);
                            showSuccessToast(t("receptionInpatients.details.toasts.followUpAppointmentCreated"));
                        } catch (apptError) {
                            console.error('Error creating follow-up appointment:', apptError);
                            showErrorToast(t("receptionInpatients.details.toasts.followUpAppointmentFailed"));
                        }
                    }

                    // Sync services with due dates into pet vaccination records
                    const petId = overlayData?.pet?.id;
                    if (petId && selectedServices.length > 0) {
                        await syncVisitServicesToVaccinations(petId, selectedServices, overlayData?.visit_date);
                    }

                    setShowCheckout(false);
                    onClose();
                    if (fetchAllTabData) fetchAllTabData(true);
                }}
                workingHours={workingHours}
            />
        </div >
    );
};

export default InpatientDetailsModal;
